import { useEffect, useRef, useState } from 'react';
import { Spinner } from '@/components/ui/Feedback';

/**
 * Просмотр PDF внутри приложения.
 *
 * Встроенный просмотрщик браузера здесь не годится: в мобильном WebView
 * открытие PDF во фрейме уводит страницу и выгружает приложение, а браузеры на
 * Chromium отказываются рисовать PDF во фрейме с песочницей. Поэтому страницы
 * рисуются самим приложением на canvas — это работает одинаково везде и не
 * зависит от плагинов клиента.
 *
 * Библиотека подключается динамически: она весит заметно больше всего
 * остального кода, а нужна только тем, кто открыл документ.
 */

/** Сколько страниц показываем: итоги ассистента короткие, а память в WebView нет. */
const MAX_PAGES = 20;

/** Предел ширины отрисовки: на плотных экранах полный масштаб съедает память. */
const MAX_SCALE = 2;

/**
 * Сколько ждать открытия документа.
 *
 * Библиотека умеет зависать молча — например, когда поток разбора не
 * запустился. Бесконечное «Открываем документ…» хуже честного отказа: из него
 * нет выхода, а файл всё это время можно было сохранить.
 */
const OPEN_TIMEOUT_MS = 15000;

function withTimeout<T>(promise: Promise<T>): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('Документ не открылся за отведённое время')), OPEN_TIMEOUT_MS)
    ),
  ]);
}

export function PdfPreview({ url }: { url: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [pages, setPages] = useState(0);
  /** Причина отказа: консоли в мессенджере нет, и это единственный канал. */
  const [reason, setReason] = useState('');

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let cancelled = false;
    setState('loading');
    container.replaceChildren();

    void (async () => {
      try {
        const [pdfjs, { default: PdfWorker }] = await Promise.all([
          import('pdfjs-dist'),
          import('pdfjs-dist/build/pdf.worker.min.mjs?worker'),
        ]);

        // Разбор идёт в отдельном потоке: без него тяжёлый документ подвешивает
        // интерфейс. Поток создаёт сборщик, а не адрес файла: по адресу браузер
        // запрашивал бы `.mjs`, который часть серверов отдаёт с типом «поток
        // байтов» — такой файл не выполняется, и просмотр зависал бы молча
        pdfjs.GlobalWorkerOptions.workerPort = new PdfWorker();

        const document = await withTimeout(pdfjs.getDocument({ url }).promise);
        if (cancelled) return;

        const total = Math.min(document.numPages, MAX_PAGES);
        setPages(document.numPages);

        for (let number = 1; number <= total; number += 1) {
          const page = await document.getPage(number);
          if (cancelled) return;

          const canvas = window.document.createElement('canvas');
          const context = canvas.getContext('2d');
          if (!context) continue;

          const width = container.clientWidth || 320;
          const base = page.getViewport({ scale: 1 });
          const scale = Math.min((width / base.width) * (window.devicePixelRatio || 1), MAX_SCALE);
          const viewport = page.getViewport({ scale });

          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.className = 'mb-2 w-full rounded-control border border-line bg-white';

          await page.render({ canvas, canvasContext: context, viewport }).promise;
          if (cancelled) return;

          container.append(canvas);
        }

        setState('ready');
      } catch (error) {
        // Текст причины показывается намеренно: консоли в мессенджере нет, и
        // без него отказ невозможно отличить от отказа по другой причине
        console.error('Просмотр PDF не открылся:', error);
        if (cancelled) return;
        setReason(error instanceof Error ? error.message : String(error));
        setState('failed');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <div className="mb-3">
      {state === 'loading' ? (
        <p className="mb-2 flex items-center gap-2 text-[12px] text-muted">
          <Spinner size={14} />
          Открываем документ…
        </p>
      ) : null}

      {state === 'failed' ? (
        <div className="mb-2">
          <p className="text-[12px] text-muted">
            Документ не удалось показать здесь. Сохраните файл — он откроется приложением
            устройства.
          </p>
          {reason ? (
            <p className="mt-1 select-text text-[10px] leading-[1.35] text-muted/80">{reason}</p>
          ) : null}
        </div>
      ) : null}

      <div ref={containerRef} className="max-h-[60vh] overflow-y-auto" />

      {state === 'ready' && pages > MAX_PAGES ? (
        <p className="mt-1 text-[11px] text-muted">
          Показаны первые {MAX_PAGES} страниц из {pages}. Полный документ — в сохранённом файле.
        </p>
      ) : null}
    </div>
  );
}
